import { UserPlanVo, UserProfile } from "@/api";
import BuyCreditsIcon from "@/assets/icons/ui/buy-credits-icon.svg";
import CreditUsageIcon from "@/assets/icons/ui/credit-usage-icon.svg";
import ManageSubscriptionIcon from "@/assets/icons/ui/manage-subscription.svg";
import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import { Button } from "@/component/Button";
import { Popover } from "antd";
import classNames from "classnames";
import Link from "next/link";
import React from "react";
import styles from "../index.module.css";
import { CreditPlanLink } from "./CreditPlanLink";

interface CreditPopoverProps {
  user: UserProfile;
  userPlan: UserPlanVo | null;
  userLoading: boolean;
  userCredits: number;
  onCreditUsageClick: () => void;
}

export const CreditPopover: React.FC<CreditPopoverProps> = ({
  user,
  userPlan,
  userLoading,
  userCredits,
  onCreditUsageClick,
}) => (
  <Popover
    key="credit-popover"
    trigger={["hover"]}
    placement="bottom"
    arrow={false}
    classNames={{
      root: "!pr-0 sm:!pr-8 !pt-1",
      body: classNames(
        "!p-2 flex bg-background-card! text-xs text-center !rounded-xl",
        styles.popoverBody
      ),
    }}
    zIndex={100}
    content={
      <>
        <div className="text-text-primary font-normal p-4 rounded-lg bg-background-elevated w-[240px] sm:w-[260px]">
          <div className="w-full flex items-center justify-between">
            <div className="flex flex-col items-start">
              <p
                className={classNames(
                  "font-medium",
                  userPlan?.expireTime ? "text-sm" : "text-base"
                )}
              >
                {userPlan?.memberShipName}
              </p>

              {userPlan?.expireTime && (
                <p className="text-xs text-graphite">
                  Valid until:{" "}
                  {new Date(userPlan.expireTime).toLocaleDateString()}
                </p>
              )}
            </div>

            <Link href="/pricing" data-disable-progress={true}>
              <Button
                type="primary"
                className="!h-7 !px-3 !text-xs"
                onClick={() => {
                  logger.event(VidMuseEvent.HeaderMenuClick, {
                    menu: "credit",
                    item: "upgrade",
                  });
                }}
              >
                Upgrade
              </Button>
            </Link>
          </div>
        </div>

        <div className="flex flex-col gap-2 mt-2">
          <Link href="/payment/sessions/portal" target="_blank">
            <Button
              type="plain"
              className="w-full justify-start h-12 rounded-lg pl-4 text-text-primary font-normal hover:bg-background-card-hover"
              icon={<ManageSubscriptionIcon className="w-6 h-6" />}
              onClick={() => {
                logger.event(VidMuseEvent.HeaderMenuClick, {
                  menu: "credit",
                  item: "manage_subscription",
                });
              }}
            >
              Manage Subscription
            </Button>
          </Link>

          <Link href="/pricing#add-ons" data-disable-progress={true}>
            <Button
              type="plain"
              className="w-full justify-start h-12 rounded-lg pl-4 text-text-primary font-normal hover:bg-background-card-hover"
              icon={<BuyCreditsIcon className="w-6 h-6" />}
              onClick={() => {
                logger.event(VidMuseEvent.HeaderMenuClick, {
                  menu: "credit",
                  item: "get_more_credits",
                });
              }}
            >
              Get More Credits
            </Button>
          </Link>

          <Button
            type="plain"
            className="w-full justify-start h-12 rounded-lg pl-4 text-text-primary font-normal hover:bg-background-card-hover"
            icon={<CreditUsageIcon className="w-6 h-6" />}
            onClick={() => {
              onCreditUsageClick();
              logger.event(VidMuseEvent.HeaderMenuClick, {
                menu: "credit",
                item: "credit_usage_details",
              });
            }}
          >
            Credit Usage Details
          </Button>
        </div>
      </>
    }
  >
    <div>
      <CreditPlanLink
        user={user}
        userLoading={userLoading}
        userPlan={userPlan}
        userCredits={userCredits}
      />
    </div>
  </Popover>
);
